// This file is part of ecbyts/ECBT.
// Copyright (C) 2026 Calvin Stefan Iost
// Licensed under the GNU Affero General Public License v3.0 (AGPL-3.0-only).

/* ================================================================
   CONTACT REGISTRY — SPEC_EMAIL_CENTRALIZATION_ADMIN §S2

   Centralized contact email resolver with sessionStorage cache.
   Replaces hardcoded personal emails across the codebase.
   ================================================================ */

import { isValidEmail, isPersonalDomain } from '../../core/session/contactRegistry.js';

const CACHE_KEY = 'ecbyts-contact-config';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const FEATURE_FLAG = true; // ADMIN_CONTACT_REGISTRY

// CONTACT-ABSENCE-001: nenhuma caixa postal fabricada — enquanto o admin não
// cadastrar emails reais em /admin/config, ausência é o estado verdadeiro e os
// consumidores devem omitir a linha de contato (não inventar endereço).
const FALLBACKS = Object.freeze({
    privacy_email: '',
    support_email: '',
    founder_email: '',
    admin_fallback_email: '',
});

/**
 * Aceita um endereco vindo do servidor SOMENTE se ele for um email de verdade.
 *
 * Por que existe: ate aqui `getContactEmail` devolvia `cached[key]` CRU e o valor
 * ia direto para o DOM e para `mailto:`. A validacao ja existia em
 * `core/session/contactRegistry` (isValidEmail/isPersonalDomain) e nao tinha
 * NENHUM consumidor de runtime — tres registries para o mesmo conceito, e o
 * unico que rodava era o que nao verificava nada. Config malformada em
 * /admin/config virava link quebrado na cara do usuario.
 *
 * Endereco pessoal (gmail/hotmail/...) tambem e recusado: contato de plataforma
 * nao pode cair na caixa particular de ninguem (§Q2 da spec).
 *
 * Recusar devolve '' — a ausencia continua sendo o estado verdadeiro
 * (CONTACT-ABSENCE-001), nunca uma caixa fabricada.
 */
function _aceitarEmail(valor: unknown): string {
    if (typeof valor !== 'string' || valor.trim() === '') return '';
    const limpo = valor.trim();
    if (!isValidEmail(limpo)) {
        console.warn('[contactRegistry] valor recusado — nao e email valido');
        return '';
    }
    if (isPersonalDomain(limpo)) {
        console.warn('[contactRegistry] valor recusado — dominio pessoal em contato de plataforma');
        return '';
    }
    return limpo;
}

/**
 * Fetch contact config from Edge Function with caching.
 * @returns {Promise<Record<string, string>>}
 */
export async function fetchContactConfig(): Promise<Record<string, string>> {
    if (!FEATURE_FLAG) return { ...FALLBACKS };

    const cached = _readCache();
    if (cached) return cached;

    try {
        const res = await fetch('/admin/config', {
            method: 'GET',
            headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const config = {
            privacy_email: _aceitarEmail(data.privacyEmail) || FALLBACKS.privacy_email,
            support_email: _aceitarEmail(data.supportEmail) || FALLBACKS.support_email,
            founder_email: _aceitarEmail(data.founderEmail) || FALLBACKS.founder_email,
            admin_fallback_email: _aceitarEmail(data.adminFallbackEmail) || FALLBACKS.admin_fallback_email,
        };
        _writeCache(config);
        return config;
    } catch (err) {
        console.warn('[contactRegistry] fetch failed, using fallbacks:', (err as any)?.message);
        return { ...FALLBACKS };
    }
}

/**
 * Get a single contact email by key.
 * @param {string} key - e.g. 'founder_email', 'privacy_email'
 * @returns {string} email do cache/servidor, ou '' quando não há valor real (CONTACT-ABSENCE-001)
 */
export function getContactEmail(key: string): string {
    const cached = _readCache();
    // Revalida na LEITURA, nao so na escrita: o cache mora em sessionStorage e
    // pode ter sido escrito por uma versao anterior ou adulterado no navegador.
    if (cached && cached[key]) {
        const aceito = _aceitarEmail(cached[key]);
        if (aceito) return aceito;
    }
    const fallbacks = FALLBACKS as Record<string, string>;
    return (fallbacks[key] || '') as string;
}

/**
 * Hydrate DOM elements with data-contact-slot attributes.
 * Non-blocking: fires-and-forgets the fetch.
 */
export function hydrateContactSlots(): void {
    const slots = document.querySelectorAll('[data-contact-slot]');
    if (slots.length === 0) return;

    const fallbacks = FALLBACKS as Record<string, string>;
    // Immediate fallback hydration — slot sem valor é PULADO (CONTACT-ABSENCE-001):
    // nunca escrever email fabricado por cima do texto estático da página.
    slots.forEach((el) => {
        const elAny = el as any;
        const key = elAny.dataset.contactSlot.replace(/-/g, '_') + '_email';
        const fallbackValue = (fallbacks[key] || '') as string;
        if (fallbackValue && (!el.textContent || el.textContent.trim() === '')) {
            el.textContent = fallbackValue;
        }
    });

    // Async refresh from server
    fetchContactConfig()
        .then((config) => {
            slots.forEach((el) => {
                const elAny = el as any;
                const raw = elAny.dataset.contactSlot || '';
                const key = raw.replace(/-/g, '_') + '_email';
                const value = config[key];
                if (value) el.textContent = value;
            });
        })
        .catch((e) => {
            console.debug('[contactRegistry]', (e as any)?.message || e);
        });
}

function _readCache(): any {
    try {
        const raw = sessionStorage.getItem(CACHE_KEY) || '';
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed.ts || Date.now() - parsed.ts > CACHE_TTL_MS) return null;
        return parsed.data;
    } catch (e) {
        console.debug('[contactRegistry] readCache:', (e as any)?.message || e);
        return null;
    }
}

function _writeCache(data: any): void {
    try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
    } catch (e) {
        console.debug('[contactRegistry] writeCache:', (e as any)?.message || e);
        // Quota or privacy mode
    }
}
